import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

// Import your videos with different resolutions
import videoHighRes from './videos/showreel-high.mp4';
import videoMediumRes from './videos/showreel-medium.mp4';
import videoLowRes from './videos/showreel-low.mp4';

const CustomButton = ({ to, children, className }) => {
  return (
    <Link to={to} className={`custom-btn ${className}`}>
      {children}
      <span className="btn-underline"></span>
    </Link>
  );
};

const HomePage = () => {
  // Single source of truth for video/loading state
  const [videoState, setVideoState] = useState({
    src: null,
    isLoaded: false,
    isPlaying: false
  });
  
  const videoRef = useRef(null);
  
  // Aggressive function to force video playback
  const forcePlayVideo = () => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    console.log('🔄 Attempting to force play video');
    
    // Make extra sure these attributes are set
    video.muted = true;
    video.playsInline = true;
    
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise.then(() => {
        console.log('✅ Video play successful');
        setVideoState(prevState => ({
          ...prevState,
          isPlaying: true
        }));
      }).catch(error => {
        console.warn('⚠️ Autoplay prevented:', error);
        
        // Try playing again after a short delay
        setTimeout(() => {
          video.play().catch(err => {
            console.error('❌ Second play attempt failed:', err);
          });
        }, 500);
      });
    }
  };
  
  // Set up periodic attempts to play the video
  useEffect(() => {
    // Only proceed if we have both a video element and a source
    if (!videoRef.current || !videoState.src) return;
    
    // If already playing, no need to continue
    if (videoState.isPlaying) return;
    
    // Try to play immediately after a brief delay
    const initialPlayTimer = setTimeout(() => {
      forcePlayVideo();
    }, 300);
    
    // Set up an interval to keep trying to play every second
    const playInterval = setInterval(() => {
      if (!videoState.isPlaying) {
        forcePlayVideo();
      } else {
        clearInterval(playInterval);
      }
    }, 1000);
    
    return () => {
      clearTimeout(initialPlayTimer);
      clearInterval(playInterval);
    };
  }, [videoState.src, videoState.isPlaying]);
  
  // Select video quality and set up loading timeout
  useEffect(() => {
    let videoSrc = videoLowRes; // Default to low resolution
    
    // Try to detect connection speed if available
    if (navigator.connection) {
      const connection = navigator.connection;
      const effectiveType = connection.effectiveType;
      const downlink = connection.downlink || 0;
      
      console.log(`Connection: ${effectiveType}, Downlink: ${downlink} Mbps`);
      
      if ((effectiveType === '4g' && downlink > 7) || downlink > 7) {
        videoSrc = videoHighRes;
        console.log('highres video selected');
      } else if ((effectiveType === '4g' && downlink > 2) || downlink > 2) {
        videoSrc = videoMediumRes;
        console.log('medium res video selected');
      } else {
        videoSrc = videoLowRes;
        console.log('low res video selected');
      }
    }
    
    // Set initial video source
    setVideoState(prevState => ({
      ...prevState,
      src: videoSrc
    }));
    
    // Set a guaranteed timeout to show video after 8 seconds
    // This ensures we never get stuck on the loading screen
    const timeoutId = setTimeout(() => {
      console.log('⏱️ Timeout triggered: forcing video to show');
      setVideoState(prevState => ({
        ...prevState,
        isLoaded: true
      }));
      
      // Also try to play the video if it hasn't started
      if (videoRef.current && !videoState.isPlaying) {
        forcePlayVideo();
      }
    }, 8000);
    
    return () => clearTimeout(timeoutId);
  // We intentionally exclude videoState.isPlaying from the dependency array
  // and use the current state inside the timeout callback instead
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Handle video events with a simple approach
  const handleVideoLoadedData = () => {
    console.log('🎬 Video data loaded, showing video');
    setVideoState(prevState => ({
      ...prevState,
      isLoaded: true
    }));
    
    // Try to play the video when data is loaded
    forcePlayVideo();
  };
  
  // Handle video playing status
  const handleVideoPlaying = () => {
    console.log('▶️ Video is now playing');
    setVideoState(prevState => ({
      ...prevState,
      isPlaying: true,
      isLoaded: true // Ensure loaded state is also set
    }));
  };
  
  // Handle video error
  const handleVideoError = (e) => {
    console.error('❌ Video error:', e);
    
    // Try to fall back to a lower quality
    if (videoState.src === videoHighRes) {
      console.log('⬇️ Falling back to medium resolution');
      setVideoState(prevState => ({
        ...prevState,
        src: videoMediumRes
      }));
    } else if (videoState.src === videoMediumRes) {
      console.log('⬇️ Falling back to low resolution');
      setVideoState(prevState => ({
        ...prevState,
        src: videoLowRes
      }));
    } else {
      // If even low quality fails, just mark as loaded to hide loader
      console.log('🔄 All video formats failed, showing loader');
      setVideoState(prevState => ({
        ...prevState,
        isLoaded: true
      }));
    }
  };
  
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          {/* Loading animation - shown only while video is loading */}
          {!videoState.isLoaded && (
            <svg 
              className="loading-frame" 
              viewBox="0 0 16 9" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                className="loading-path" 
                d="M 0,9 L 0,0 L 16,0 L 16,9 L 0,9" 
              />
            </svg>
          )}
          
          {/* Background video */}
          {videoState.src && (
            <video
              ref={videoRef}
              className={`hero-video ${videoState.isLoaded ? 'loaded' : ''}`}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              onLoadedData={handleVideoLoadedData}
              onPlaying={handleVideoPlaying}
              onError={handleVideoError}
            >
              <source src={videoState.src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          )}
        </div>
        
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h2 className="hero-title">for stories that resonate</h2>
            <div className="hero-text">
              We bring documentary-quality storytelling to TV, branded content and live events, 
              capturing authentic moments that connect with audiences.
            </div>
            <CustomButton to="/work" className="custom-btn custom-btn-primary">
              View our work
            </CustomButton>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 grid-cols-3">
            <div className="service-card">
              <h3 className="service-title">Branded Documentary</h3>
              <p className="service-description">
                Authentic storytelling that connects audiences to your brand's values and mission, 
                crafted with documentary filmmaking techniques.
              </p>
            </div>
            <div className="service-card">
              <h3 className="service-title">Live Event Capture</h3>
              <p className="service-description">
                Cinematic documentation of brand activations, conferences, and engagement campaigns
                that preserve the energy and impact of your events.
              </p>
            </div>
            <div className="service-card">
              <h3 className="service-title">B2B & Corporate</h3>
              <p className="service-description">
                Strategic content for business audiences that communicates complex messages
                with clarity, sophistication, and emotional resonance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to bring your story to life?</h2>
            <p className="cta-text">
              Let's discuss how our documentary approach can elevate your next campaign or event.
            </p>
            <Link to="/contact" className="custom-btn custom-btn-primary">Get in touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;